"use client";

import React, { useCallback, useState } from "react";
import {
  BadgeIndianRupee,
  Package,
  Plus,
  RefreshCw,
  Truck,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { ColumnDef } from "@tanstack/react-table";
import type { RootState } from "@/store/store";
import { Button } from "@/components/ui/button";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { useDashboardPanel } from "@/hooks/useDashboardPanel";

interface Order {
  id: string;
  order_number?: number;
  created_at?: string;
  updated_at?: string;
  current_order_stage?: string;
  current_payment_stage?: string;
  supported_payment_type?: string[];
  customer?:
    | string
    | {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone_number?: string;
      };
  shipping_address?: {
    address_line_one?: string;
    address_line_two?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  };
  items?: number | Array<any> | Record<string, any>;
  calculation?: {
    grand_total?: number;
    grand_total_int?: number;
    items_sub_total?: number;
  };
}

function formatMoney(amount: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function getOrderTotal(order: Order) {
  const calc = order.calculation;
  if (typeof calc?.grand_total === "number" && calc.grand_total > 0)
    return calc.grand_total;
  if (typeof calc?.grand_total_int === "number" && calc.grand_total_int > 0)
    return calc.grand_total_int / 100;
  if (typeof calc?.items_sub_total === "number" && calc.items_sub_total > 0)
    return calc.items_sub_total / 100;
  return 0;
}

function getItemsCount(items: Order["items"]) {
  if (typeof items === "number") return items;
  if (Array.isArray(items)) {
    return items.reduce((acc, item) => {
      if (
        item &&
        typeof item === "object" &&
        typeof item.quantity === "number"
      ) {
        return acc + item.quantity;
      }
      return acc + 1;
    }, 0);
  }
  if (items && typeof items === "object") {
    if (typeof (items as any).quantity === "number") {
      return (items as any).quantity;
    }
    return Object.keys(items).length;
  }
  return 0;
}

function formatCustomer(customer: Order["customer"]) {
  if (!customer) return "N/A";
  if (typeof customer === "string") return customer;
  const name =
    `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  return name || customer.email || customer.phone_number || "N/A";
}

function formatRoute(address?: Order["shipping_address"]) {
  if (!address) return "N/A";
  return (
    [address.city, address.state].filter(Boolean).join(", ") ||
    address.postal_code ||
    "N/A"
  );
}

export default function ActiveOrders() {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const { refreshPanel, isRefreshing } = useDashboardPanel("activeOrders");
  const reduxOrders = useSelector(
    (state: RootState) => state.dashboard?.activeOrders ?? [],
  );

  const displayOrders: Order[] =
    reduxOrders && Array.isArray(reduxOrders) ? (reduxOrders as Order[]) : [];

  const handleAddOrder = () => {
    console.log("Add new order");
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  }, []);

  const columns: ColumnDef<Order>[] = [
    {
      accessorKey: "id",
      header: "ORDER",
      cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {`#${row.original.id.slice(-6)}`}
            </span>
            {row.original.order_number ? (
              <span className="text-xs text-muted-foreground">
                Order #{row.original.order_number}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {row.original.created_at
              ? new Date(row.original.created_at).toLocaleDateString()
              : "Created recently"}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "customer",
      header: "CUSTOMER",
      cell: ({ row }) => (
        <div className="space-y-1">
          <p className="font-medium text-foreground">
            {formatCustomer(row.original.customer)}
          </p>
          <p className="text-xs text-muted-foreground">
            {typeof row.original.customer === "object"
              ? row.original.customer.email || "No email"
              : "Customer reference"}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "STAGES",
      cell: ({ row }) => (
        <div className="space-y-2">
          <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
            {String(
              row.original.current_order_stage || "CREATED",
            ).toLowerCase()}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <BadgeIndianRupee className="h-3.5 w-3.5" />
            {String(
              row.original.current_payment_stage || "CREATED",
            ).toLowerCase()}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "items",
      header: "ITEMS",
      cell: ({ row }) => {
        const count = getItemsCount(row.original.items);
        const paymentTypes = row.original.supported_payment_type || [];
        return (
          <div className="space-y-1">
            <div className="font-medium text-foreground">
              {count} item{count === 1 ? "" : "s"}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Package className="h-3.5 w-3.5" />
              {Array.isArray(row.original.items)
                ? `${row.original.items.length} line items`
                : "Summary view"}
            </div>
            {paymentTypes.length ? (
              <p className="text-xs text-muted-foreground">
                {paymentTypes.slice(0, 2).join(" • ")}
              </p>
            ) : null}
          </div>
        );
      },
    },
    {
      accessorKey: "shipping_address",
      header: "ROUTE",
      cell: ({ row }) => {
        const address = row.original.shipping_address;
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <Truck className="h-3.5 w-3.5 text-muted-foreground" />
              {formatRoute(address)}
            </div>
            <p className="text-xs text-muted-foreground">
              {address?.address_line_one || "Address not set"}
              {address?.postal_code ? ` • ${address.postal_code}` : ""}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "total",
      header: "TOTAL",
      cell: ({ row }) => (
        <div className="space-y-1">
          <span className="font-semibold text-foreground">
            {formatMoney(getOrderTotal(row.original))}
          </span>
          <p className="text-xs text-muted-foreground">
            {row.original.updated_at
              ? `Updated ${new Date(row.original.updated_at).toLocaleDateString()}`
              : "Awaiting final total"}
          </p>
        </div>
      ),
    },
  ];

  return (
    <TableContainer
      className="w-full"
      headerLeft={
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">
            Active Orders
          </h2>
          <p className="text-xs text-muted-foreground">
            {displayOrders.length} active order
            {displayOrders.length !== 1 ? "s" : ""} with stage, payment, route,
            and total details
          </p>
        </div>
      }
      headerRight={
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshPanel}
          disabled={isRefreshing}
          className="h-8 w-8 p-0"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </Button>
      }
    >
      <DataView
        columns={columns}
        data={displayOrders}
        title=""
        searchQuery={searchQuery}
        onSearch={handleSearch}
        searchPlaceholder="Search orders..."
        pagination={{
          page: pageIndex,
          total: displayOrders.length,
          pageSize: displayOrders.length,
          totalPages: 1,
        }}
        pageIndex={pageIndex}
        onPageChange={setPageIndex}
        loading={false}
        actionButtons={[
          {
            label: "Add Order",
            onClick: handleAddOrder,
            variant: "outline",
            icon: <Plus className="w-4 h-4" />,
          },
        ]}
        cardWrapper
        cardWrapperClassName="border-border/60 bg-card/85 backdrop-blur-xl shadow-theme-sm"
        emptyMessage="No active orders found."
      />
    </TableContainer>
  );
}
